// ConsultationInterface.tsx

import React, { useState, useRef, useEffect } from "react";
import { apiService } from '@/api/api';
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useScreenAwake } from '@/hooks/useScreenAwake';
import useTranscribeMedicalConsultation from '@/hooks/use-transcribe-medical-consultation';
import { useRealtimeConsultation, formatSOAPPreview, getSOAPCounts } from '@/hooks/useRealtimeConsultation';
import { realtimeApi } from '@/services/realtimeApi';
import { Mic, MicOff, Play, BookText, Languages, Headphones, X, Loader2, Wifi, WifiOff, FileText } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import SearchInput from "@/components/clients/SearchClientInput";
import { format } from 'date-fns';
import { formatFileSize, formatTime as formatUploadTime } from '@/utils/s3MultipartUpload';

type RecordingStatus = 'idle' | 'recording' | 'paused';
type ConsultationPhase = 'pre_start' | 'recording' | 'uploading_transcription' | 'finishing';

// Real-time configuration
const REALTIME_CONFIG = {
  minWordsBeforeFlush: 10,
  maxWordsBeforeForceFlush: 60,
  enableRealTimeSOAP: true,
};

const APPSYNC_CONFIG = {
  apiUrl: import.meta.env.VITE_APPSYNC_URL || '',
  region: import.meta.env.VITE_AWS_REGION || 'us-east-1',
};

// Initialize API on module load
if (APPSYNC_CONFIG.apiUrl) {
  realtimeApi.initialize(APPSYNC_CONFIG);
}

const ConsultationInterface = () => {
  const [title, setTitle] = useState("");
  const [selectedClient, setSelectedClient] = useState<any>(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [clients, setClients] = useState<any[]>([]);
  const [clientsLoading, setClientsLoading] = useState(true);

  // Recording states - SIMPLIFIED
  const [recordingTime, setRecordingTime] = useState(0);
  const [hasStartedRecording, setHasStartedRecording] = useState(false);
  // Note: isPaused is now managed by the transcription hook (isTranscribePaused)
  
  // New state variables for accurate time tracking
  const [recordingStartTime, setRecordingStartTime] = useState<number | null>(null);
  const [pausedDuration, setPausedDuration] = useState(0);
  const [lastPauseTime, setLastPauseTime] = useState<number | null>(null);

  const [isUploadingTranscription, setIsUploadingTranscription] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);

  // New state variables for real-time processing
  const [isFinishing, setIsFinishing] = useState(false);
  const [finishProgress, setFinishProgress] = useState<number>(0);
  const [showSOAPPreview, setShowSOAPPreview] = useState(false);

  // Audio recording states (removed - using only AWS Transcribe)
  const audioStreamRef = useRef<MediaStream | null>(null);

  const [session_id, setSessionId] = useState<string | null>(null);
  const [sessionDate, setSessionDate] = useState<string | null>(null);
  const [sessionTime, setSessionTime] = useState<string | null>(null);
  const [creatingSession, setCreatingSession] = useState(false);
  const [encounterSettingsOpen, setEncounterSettingsOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState("soap");
  const [selectedLanguage, setSelectedLanguage] = useState("Auto-detect");
  const [selectedMic, setSelectedMic] = useState<string | null>(null);
  const [mics, setMics] = useState<MediaDeviceInfo[]>([]);
  const [micPermission, setMicPermission] = useState<boolean | null>(null);
  const [searchInputKey, setSearchInputKey] = useState(0);

  const [consultationPhase, setConsultationPhase] = useState<ConsultationPhase>('pre_start');

  const titleInputRef = useRef<HTMLInputElement>(null);
  const wakeIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  // AWS Transcribe Hook with Diarization
  const {
    isTranscribing,
    isPaused: isTranscribePaused,
    currentTranscript,
    transcribedSegments,
    isWaitingForFinalTranscript,
    startTranscription,
    pauseTranscription,
    resumeTranscription,
    stopTranscription,
    waitForFinalTranscripts,
    getFullTranscript,
    getTranscriptionData,
    clearLocalStorage
  } = useTranscribeMedicalConsultation({
    selectedLanguage: selectedLanguage, // Pass the selected language directly (Auto-detect, English, or Arabic)
    sessionId: session_id || ''
  });

  // Screen Wake
  const {
    isActive: screenAwakeActive,
    enable: enableScreenAwake,
    disable: disableScreenAwake,
    error: screenAwakeError
  } = useScreenAwake();

  // Real-time consultation hook
  const [realtimeState, realtimeActions] = useRealtimeConsultation({
    sessionId: session_id || '',
    ...REALTIME_CONFIG,
    onSentenceSent: (sentence) => {
      console.log(`[Realtime] Sent: ${sentence.text.substring(0, 50)}...`);
    },
    onDeltaReceived: (delta) => {
      console.log(`[Realtime] Deltas: S=${delta.delta_subjective.length}, O=${delta.delta_objective.length}, P=${delta.delta_plan.length}`);
    },
    onError: (error) => {
      console.error('[Realtime] Error:', error);
    },
  });

  const templates = ["soap"];

  // --- Helper Functions ---

  // Session ID Validation Function
  const validateSessionId = (id: any): { valid: boolean; error?: string } => {
    if (!id) {
      return { valid: false, error: "No session ID provided" };
    }
    
    if (typeof id !== 'string') {
      return { valid: false, error: "Session ID must be a string" };
    }
    
    // Check for valid UUID format or other expected formats
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const alphanumericRegex = /^[a-zA-Z0-9_-]+$/;
    
    if (!uuidRegex.test(id) && !alphanumericRegex.test(id)) {
      return { valid: false, error: "Invalid session ID format" };
    }
    
    if (id.length < 3 || id.length > 100) {
      return { valid: false, error: "Session ID length is invalid" };
    }
    
    return { valid: true };
  };

  // --- useEffects ---
  useEffect(() => {
    apiService.getClients()
          .then(setClients)
          .catch(err => {
            console.error('❌ Error fetching clients:', err);
            toast({ title: "Error", description: "Failed to fetch client list.", variant: "destructive" });
          })
          .finally(() => setClientsLoading(false));
    

    const getMicrophones = async () => {
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true });
        const devices = await navigator.mediaDevices.enumerateDevices();
        const microphones = devices.filter(device => device.kind === 'audioinput');
        setMics(microphones);
        if (microphones.length > 0 && !selectedMic) {
          setSelectedMic(microphones[0].deviceId);
        }
        setMicPermission(true);
      } catch (error) {
        console.error("Error accessing media devices:", error);
        setMicPermission(false);
      }
    };
    
    getMicrophones();
  }, []);

  // Timer effect - uses actual elapsed time instead of incremental updates
  useEffect(() => {
    console.log(`Timer effect triggered - isTranscribing: ${isTranscribing}, isPaused: ${isTranscribePaused}, startTime: ${recordingStartTime}`);
    let animationFrameId: number;

    const updateTimer = () => {
      if (isTranscribing && !isTranscribePaused && recordingStartTime) {
        const now = Date.now();
        const elapsed = Math.floor((now - recordingStartTime - pausedDuration) / 1000);
        setRecordingTime(elapsed);
        animationFrameId = requestAnimationFrame(updateTimer);
      }
    };

    if (isTranscribing && !isTranscribePaused && recordingStartTime) {
      console.log('Starting timer updates...');
      updateTimer();
    }

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [isTranscribing, isTranscribePaused, recordingStartTime, pausedDuration]);

  // Visibility change listener to ensure timer stays accurate
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isTranscribing && !isTranscribePaused) {
        // Force a timer update when tab becomes visible again
        if (recordingStartTime) {
          const now = Date.now();
          const elapsed = Math.floor((now - recordingStartTime - pausedDuration) / 1000);
          setRecordingTime(elapsed);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isTranscribing, isTranscribePaused, recordingStartTime, pausedDuration]);


  // Show screen wake error if any
  useEffect(() => {
    if (screenAwakeError && consultationPhase === 'recording') {
      console.warn('Screen wake error:', screenAwakeError);
    }
  }, [screenAwakeError, consultationPhase]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (wakeIntervalRef.current) {
        clearInterval(wakeIntervalRef.current);
      }
      if (screenAwakeActive) {
        disableScreenAwake();
      }
    };
  }, [screenAwakeActive, disableScreenAwake]);

  // Warn user before leaving page during recording
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (consultationPhase === 'recording' && (isTranscribing || hasStartedRecording)) {
        // Attempt to save transcription data to localStorage before leaving
        const transcriptionData = getTranscriptionData();
        if (transcriptionData.segments && transcriptionData.segments.length > 0) {
          localStorage.setItem(`consultation_backup_${session_id}`, JSON.stringify({
            ...transcriptionData,
            backup_time: new Date().toISOString(),
            recording_time: recordingTime
          }));
        }

        // Show browser warning
        e.preventDefault();
        e.returnValue = 'You have an active recording. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [consultationPhase, isTranscribing, hasStartedRecording, session_id, recordingTime, getTranscriptionData]);

  // Bridge AWS Transcribe → Real-Time Hook
  // Track last processed segment index to avoid duplicates
  const lastProcessedSegmentIndexRef = useRef<number>(-1);

  useEffect(() => {
    if (!isTranscribing || !session_id) return;

    // Process only new segments
    const newSegments = transcribedSegments.slice(lastProcessedSegmentIndexRef.current + 1);

    newSegments.forEach((segment, idx) => {
      if (segment && segment.content && !segment.is_partial) {
        const speaker = segment.speaker_label || 'Speaker 1';
        realtimeActions.addTranscriptSegment(
          segment.content,
          speaker,
          true // isFinal
        );
      }
    });

    // Update last processed index
    if (transcribedSegments.length > 0) {
      lastProcessedSegmentIndexRef.current = transcribedSegments.length - 1;
    }
  }, [transcribedSegments, isTranscribing, session_id]);

  const getUserFirstName = () => {
    if (!user || !user.attributes || !user.attributes.name) return "User";
    const fullName = user.attributes.name;
    return fullName.split(' ')[0];
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60).toString().padStart(2, '0');
    const secs = (seconds % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
  };

  // Enhanced session creation with ID validation
  const createSession = async () => {
    try {
      const client_id = selectedClient?.client_id || null;
      if (!client_id) {
        throw new Error('Client selection is required to create a session');
      }
      
      const now = new Date();
      const newSessionDate = format(now, 'yyyy-MM-dd');
      const newSessionTime = format(now, 'HH:mm');
      setSessionDate(newSessionDate);
      setSessionTime(newSessionTime);
      
      const session_title = title && title.trim() ? title : 'New Consultation';
      const sessionData = {
        session_title,
        session_type: 'consultation' as 'consultation',
        sessionDate: newSessionDate,
        sessionTime: newSessionTime,
        session_timestamp: now.toISOString(),
        confirmed: false,
        session_status: 'processing'
      };
      
      console.log("Creating session with data:", sessionData);
      const result = await apiService.createSession(client_id, sessionData);
      console.log("Session creation response:", result);
      
      const sessionId = result?.data?.session_id || 
                       result?.session_id || 
                       result?.id || 
                       result?.data?.id ||
                       null;
      
      // Validate session ID
      const validation = validateSessionId(sessionId);
      if (!validation.valid) {
        console.error("Invalid session ID received:", sessionId, validation.error);
        throw new Error(validation.error || 'Invalid session ID received from server');
      }
      
      console.log("Valid session ID received:", sessionId);
      return sessionId;
    } catch (error: any) {
      console.error("Error in createSession:", error);
      throw error;
    }
  };

  // Start microphone for transcription only
  const startMicrophone = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000,
          ...(selectedMic && { deviceId: { exact: selectedMic } })
        }
      });

      audioStreamRef.current = stream;
      console.log("Microphone started for transcription");
    } catch (err) {
      console.error('Failed to start microphone:', err);
      throw err;
    }
  };

  // Stop recording and transcription
  const stopRecording = async () => {
    // Stop microphone stream
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(track => track.stop());
      audioStreamRef.current = null;
      console.log("Microphone stopped");
    }

    // Stop AWS Transcribe and wait for final transcripts
    if (isTranscribing || transcribedSegments.length > 0) {
      console.log("Waiting for final transcripts from AWS Transcribe...");
      await waitForFinalTranscripts();
      stopTranscription();
      console.log("AWS Transcribe stopped");
    }
  };

  // Start recording with accurate time tracking
  const startRecordingAutomatically = async () => {
    console.log("Starting automatic recording...");
    console.log(`Current state - hasStartedRecording: ${hasStartedRecording}, isTranscribing: ${isTranscribing}, isPaused: ${isTranscribePaused}`);

    // Enable screen wake
    if (!screenAwakeActive) {
      try {
        await enableScreenAwake();
        if (wakeIntervalRef.current) clearInterval(wakeIntervalRef.current);
        wakeIntervalRef.current = setInterval(() => {
          if (document.visibilityState === 'visible') {
            enableScreenAwake().catch(() => {});
          }
        }, 45000);
      } catch (err) {
        console.warn('Failed to enable screen wake (non-critical):', err);
      }
    }

    try {
      // Reset recording only on first start
      if (!hasStartedRecording) {
        console.log("First recording start - initializing timer");
        setRecordingTime(0);

        // Set initial recording start time BEFORE changing status
        const startTime = Date.now();
        setRecordingStartTime(startTime);
        setPausedDuration(0);
        setLastPauseTime(null);
        console.log(`Recording start time initialized: ${startTime}`);
      }

      // Check if we have microphone permission first
      if (micPermission === false) {
        throw new Error("Microphone permission denied");
      }

      // Start both audio recording and transcription
      console.log("Starting audio recording and transcription...");

      // Start microphone for transcription (only on first start)
      if (!hasStartedRecording) {
        try {
          await startMicrophone();
          console.log("Microphone started successfully");
        } catch (micErr) {
          console.error('Failed to start microphone:', micErr);
          // Reset on failure
          throw micErr; // Can't continue without microphone
        }
      }

      // Start AWS Transcribe (only on first start)
      try {
        if (!hasStartedRecording) {
          await startTranscription();
          console.log("AWS Transcribe started successfully");
          // Mark recording as started only after successful initialization
          setHasStartedRecording(true);
        }
      } catch (transcribeErr) {
        console.error('Failed to start AWS Transcribe:', transcribeErr);
        // Continue recording even if transcription fails
        // User can still record without transcription
        if (!hasStartedRecording) {
          setHasStartedRecording(true); // Still mark as started even if transcription fails
        }
      }

    } catch (err: any) {
      console.error('Error in startRecordingAutomatically:', err);
      let errorMessage = "Could not access your microphone.";
      
      if (err.message === "Microphone permission denied") {
        errorMessage = "Microphone access was denied. Please allow microphone access in your browser settings and refresh the page.";
      } else if (err instanceof DOMException) {
        switch (err.name) {
          case 'NotAllowedError':
            errorMessage = "Microphone access was denied. Please allow microphone access and try again.";
            break;
          case 'NotFoundError':
            errorMessage = "No microphone found. Please connect a microphone and try again.";
            break;
          case 'NotReadableError':
            errorMessage = "Microphone is already in use by another application.";
            break;
          case 'OverconstrainedError':
            errorMessage = "Selected microphone is not available. Please select a different microphone.";
            break;
          default:
            errorMessage = err.message || "Could not start recording. Please check your microphone.";
        }
      }
      
      toast({
        title: "Microphone Error",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  // --- Handlers ---
  const handleStartConsultation = async () => {
    setCreatingSession(true);
    try {
      console.log("Creating session...");
      const newSessionId = await createSession();
      if (!newSessionId) {
        throw new Error("No session ID returned from server");
      }
      console.log("Session created with ID:", newSessionId);
      
      setSessionId(newSessionId);
      setConsultationPhase('recording');
      
      try {
        await enableScreenAwake();
        console.log('Screen wake enabled for consultation');
      } catch (err) {
        console.warn('Screen wake failed (non-critical):', err);
      }

      setCreatingSession(false);

      // Start recording immediately after a short delay to ensure state is ready
      setTimeout(async () => {
        try {
          await startRecordingAutomatically();
          console.log("Recording started successfully");
        } catch (recordErr: any) {
          console.error("Failed to start recording:", recordErr);
          toast({
            title: "Recording Error",
            description: "Could not start recording. Please check your microphone permissions.",
            variant: "destructive"
          });
        }
      }, 100); // Small delay to ensure session is ready

    } catch (err: any) {
      console.error("Failed to create session:", err);
      toast({ 
        title: "Failed to start consultation", 
        description: err.message || "Could not create session. Please try again.", 
        variant: "destructive" 
      });
      setConsultationPhase('pre_start');
      setCreatingSession(false);
    }
  };

  // Mute recording with time tracking
  const handlePauseRecording = async () => {
    console.log("Mute clicked - isTranscribing:", isTranscribing, "isPaused:", isTranscribePaused);

    if (isTranscribing && !isTranscribePaused) {
      try {
        // Track when we muted for timer calculation
        setLastPauseTime(Date.now());

        await pauseTranscription(); // Mute microphone (this sets isPaused in the hook)
        realtimeActions.pause(); // Pause real-time streaming

        console.log("Microphone muted successfully");

        toast({
          title: "Microphone muted",
          description: `${formatTime(recordingTime)} recorded. Press unmute to continue or finish to create note.`,
          variant: "default",
        });
      } catch (err) {
        console.error('Error pausing recording:', err);
        toast({
          title: "Error",
          description: "Failed to pause recording properly.",
          variant: "destructive",
        });
      }
    }
  };

  const handleResumeRecording = async () => {
    console.log("Unmute clicked - isTranscribing:", isTranscribing, "isPaused:", isTranscribePaused);

    if (isTranscribePaused) {
      try {
        // Update time tracking
        const resumeTime = Date.now();
        if (lastPauseTime) {
          const pauseDuration = resumeTime - lastPauseTime;
          setPausedDuration(prev => prev + pauseDuration);
        }
        setLastPauseTime(null);

        // Unmute microphone (this clears isPaused in the hook)
        await resumeTranscription();
        realtimeActions.resume(); // Resume real-time streaming
        console.log("Microphone unmuted");

        console.log("Recording resumed successfully");

        toast({
          title: "Microphone unmuted",
          description: "Continue with your consultation.",
          variant: "default",
        });
      } catch (err) {
        console.error('Error resuming recording:', err);
        toast({
          title: "Error",
          description: "Failed to resume recording. Please check your microphone.",
          variant: "destructive",
        });
      }
    }
  };

  // Complete transcript upload logic
  const handleFinishNote = async () => {
    console.log('handleFinishNote called');

    try {
      // Pre-flight checks
      if (!session_id) {
        throw new Error("No active session. Please restart the consultation.");
      }

      const sessionValidation = validateSessionId(session_id);
      if (!sessionValidation.valid) {
        throw new Error(sessionValidation.error || "Invalid session ID. Please restart.");
      }

      // Set finishing phase
      setConsultationPhase('finishing');
      setIsFinishing(true);
      setFinishProgress(10);

      // If muted, unmute first to ensure we capture any final audio
      if (isTranscribePaused) {
        await resumeTranscription();
        console.log("Unmuted microphone before finishing");
      }

      // Stop recording and wait for final transcripts
      await waitForFinalTranscripts();
      stopTranscription();

      // Flush remaining buffer for real-time processing
      realtimeActions.flushBuffer();
      setFinishProgress(40);

      // Stop microphone stream
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => track.stop());
        audioStreamRef.current = null;
        console.log("Microphone stopped");
      }

      // Get final transcription data
      const transcriptionData = getTranscriptionData();

      // Allow finishing even if no recording was done (user might want to cancel)
      if (!transcriptionData.segments || transcriptionData.segments.length === 0) {
        console.warn("No transcription data available, finishing without transcript");
        transcriptionData.segments = [];
      }

      console.log(`Transcription complete with ${transcriptionData.segments.length} segments`);
      console.log('[ConsultationInterface] Full transcript:', getFullTranscript());

      // Call Finisher Lambda via Real-Time API
      // This is the main operation - formats SOAP note (~0.5-1s)
      setFinishProgress(50);
      let finalResult = null;
      try {
        finalResult = await realtimeActions.finish();
        setFinishProgress(95);
        console.log('[Realtime] Final result:', finalResult);
      } catch (finishErr) {
        console.warn('Real-time finish failed, falling back to legacy:', finishErr);
        finalResult = null;
      }

      // Upload transcription to S3 in background (non-blocking)
      // This runs in parallel with navigation - no need to wait
      if (transcriptionData.segments && transcriptionData.segments.length > 0) {
        const transcriptionJson = JSON.stringify(transcriptionData, null, 2);
        const transcriptionBlob = new Blob([transcriptionJson], { type: 'application/json' });

        // Fire and forget - upload happens in background
        (async () => {
          try {
            console.log("[Background] Getting transcription upload URL...");
            const transcriptionUrlResponse = await apiService.getSessionTranscriptionUploadUrl(session_id, {
              fileName: `consultation_transcript_${Date.now()}.json`,
              transcriptionFormat: 'aws_transcribe_with_diarization',
              sourceType: 'consultation'
            });

            const transcriptionUploadUrl = transcriptionUrlResponse?.uploadUrl || transcriptionUrlResponse?.data?.uploadUrl || transcriptionUrlResponse;
            if (!transcriptionUploadUrl || typeof transcriptionUploadUrl !== 'string') {
              throw new Error("No valid transcription upload URL received from server");
            }

            console.log("[Background] Uploading transcription to S3...");
            const transcriptionUploadResponse = await apiService.uploadAudioToS3(transcriptionUploadUrl, transcriptionBlob, {
              useMultipart: false,
              onProgress: (progress) => {
                console.log(`[Background] Transcription upload progress: ${progress.percentage}%`);
              }
            });

            if (!transcriptionUploadResponse.ok) {
              throw new Error(`Transcription upload failed: ${transcriptionUploadResponse.status}`);
            }

            console.log("[Background] Transcription uploaded successfully!");
            clearLocalStorage();
            console.log("[Background] Local transcription storage cleared");
          } catch (uploadError) {
            console.error("[Background] Error uploading transcription:", uploadError);
            // Data remains in local storage for retry on next session
          }
        })();
      } else {
        console.log("No transcription to upload, skipping S3 upload");
      }

      // Note: Finisher Lambda already updates session status to 'completed' in DynamoDB
      // No need for redundant updateSessionData call here

      // Reset real-time state
      realtimeActions.reset();

      // Cleanup
      if (screenAwakeActive) {
        disableScreenAwake();
        console.log('Screen wake disabled');
      }
      if (wakeIntervalRef.current) {
        clearInterval(wakeIntervalRef.current);
        wakeIntervalRef.current = null;
      }

      // Show success with timing
      const processingTime = finalResult?.processing_duration_seconds;
      toast({
        title: "Consultation finished",
        description: processingTime
          ? `Note formatted in ${processingTime.toFixed(1)} seconds!`
          : "Your note is ready for review.",
      });

      // Navigate directly to note refinement page with data in state
      navigate(`/refine-note/${session_id}`, {
        state: {
          sessionTitle: title || 'Consultation Note',
          finalNote: finalResult?.final_note,
          enhancedSections: finalResult?.final_note ? {
            // If we have final_note, it may contain parsed sections
            // The backend should return structured data, but we pass what we have
          } : null,
          fromConsultation: true, // Flag to indicate we came from consultation flow
          processingDuration: finalResult?.processing_duration_seconds,
        }
      });

    } catch (error) {
      console.error("Error finishing note:", error);

      // User-friendly error message
      const errorMessage = error instanceof Error ? error.message : "An unexpected error occurred";
      toast({
        title: "Failed to finish consultation",
        description: errorMessage,
        variant: "destructive"
      });

      // Reset UI state on error
      setConsultationPhase('recording');
      setIsFinishing(false);
      setFinishProgress(0);
    }
  };

  const handleTitleClick = () => {
    if (consultationPhase === 'pre_start') {
      setIsEditingTitle(true);
      setTimeout(() => {
        if (titleInputRef.current) {
          titleInputRef.current.focus();
        }
      }, 100);
    }
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  };

  const handleTitleBlur = () => {
    setIsEditingTitle(false);
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      setIsEditingTitle(false);
    }
  };

  const handleClientSelect = (client: any) => {
    if (consultationPhase === 'pre_start') {
      if (client && client.client_id) {
        setSelectedClient(client);
      } else {
        setSelectedClient(null);
      }
    }
  };

  const handleClearClient = () => {
    if (consultationPhase === 'pre_start') {
      setSelectedClient(null);
      setSearchInputKey(Date.now());
    }
  };

  const handleClientCreated = (newClient: any) => {
    // Add the new client to the local clients list
    setClients(prev => [newClient, ...prev]);
    console.log("New client created and added to list:", newClient);
  };

  // --- UI ---
  return (
    <div className="bg-white rounded-lg shadow-sm p-3 sm:p-6 w-full mb-6 relative">
      {/* Spinner Overlay for Initial Session Creation */}
      {creatingSession && (
        <div className="absolute inset-0 bg-white bg-opacity-90 z-50 flex items-center justify-center rounded-lg">
          <div className="flex flex-col items-center">
            <div className="w-8 h-8 border-4 border-gray-300 border-t-black rounded-full animate-spin mb-4"></div>
            <span className="text-lg text-gray-600">Starting consultation...</span>
          </div>
        </div>
      )}

      {/* Transcription Uploading/Processing Overlay */}
      {isUploadingTranscription && consultationPhase === 'uploading_transcription' && (
        <div className="absolute inset-0 bg-white bg-opacity-80 flex flex-col items-center justify-center z-50 rounded-lg">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
          <p className="mt-4 text-lg text-gray-700 font-medium">
            Uploading transcription data...
          </p>
          <div className="flex flex-col items-center gap-3 w-full max-w-md mx-auto mt-4">
            <div className="flex w-full justify-between items-center">
              <span className="text-sm text-gray-600">Upload progress</span>
              <span className="text-sm text-gray-600">{uploadProgress}%</span>
            </div>
            <Progress value={uploadProgress} className="w-full h-2" />


            {uploadProgress === 100 && (
              <p className="text-sm text-green-600 mt-2">
                Upload complete! Processing your consultation...
              </p>
            )}
          </div>
        </div>
      )}

      {/* Finishing overlay with progress */}
      {isFinishing && consultationPhase === 'finishing' && (
        <div className="absolute inset-0 bg-white bg-opacity-90 flex flex-col items-center justify-center z-50 rounded-lg">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
          <p className="mt-4 text-lg text-gray-700 font-medium">
            {finishProgress < 60 ? 'Saving transcription...' : 'Formatting your note...'}
          </p>
          <Progress value={finishProgress} className="w-full h-2 max-w-md mt-4" />
        </div>
      )}

      <div className="flex flex-col min-h-[400px] py-4 sm:py-8">
        <div className="flex items-center justify-between text-xs sm:text-sm text-gray-500 mb-4">
          <span>{format(new Date(), "EEEE, MMMM d, yyyy")}</span>
          {consultationPhase === 'recording' && (
            <div className={`flex items-center gap-1 text-xs ${realtimeState.isOnline ? 'text-green-600' : 'text-red-600'}`}>
              {realtimeState.isOnline ? (
                <><Wifi className="w-3 h-3" /> Online</>
              ) : (
                <><WifiOff className="w-3 h-3" /> Offline (queuing)</>
              )}
            </div>
          )}
        </div>
        
        {/* Title and Client Search Row */}
        <div className="flex flex-col sm:flex-row items-start justify-between mb-6 sm:mb-8 gap-4 sm:gap-0">
          {/* Session Title */}
          <div className="w-full sm:w-2/5">
            {isEditingTitle && consultationPhase === 'pre_start' ? (
              <input
                ref={titleInputRef}
                value={title}
                onChange={handleTitleChange}
                onBlur={handleTitleBlur}
                onKeyDown={handleTitleKeyDown}
                className="w-full text-xl sm:text-3xl font-light text-gray-600 placeholder:text-gray-400 bg-transparent border-0 border-b border-gray-300 focus:border-gray-500 outline-none px-0 py-2 transition-colors"
                placeholder="Click to add session title"
                autoFocus
              />
            ) : (
              <div
                className={`w-full text-xl sm:text-3xl font-light px-0 py-2 transition-colors border-b border-gray-300 ${
                  consultationPhase !== 'pre_start' 
                    ? 'text-gray-600 cursor-default'
                    : 'text-gray-400 cursor-pointer hover:text-gray-600'
                }`}
                onClick={handleTitleClick}
              >
                {title || "Click to add session title"}
              </div>
            )}
          </div>
          
          {/* Client Search */}
          <div className="w-full sm:w-2/5 flex flex-col items-start sm:items-end justify-start">
            <div className="relative w-full">
              <SearchInput
                key={searchInputKey}
                items={clients}
                onSelect={handleClientSelect}
                isLoading={clientsLoading}
                placeholder="Search client name..."
                className=""
                getDisplayValue={(client) => `${client.first_name || ''} ${client.last_name || ''}`.trim()}
                getItemId={(client) => client.client_id}
                filterFunction={(client, searchTerm) => {
                  const fullName = `${client.first_name || ''} ${client.last_name || ''}`.toLowerCase();
                  const email = (client.email || '').toLowerCase();
                  const phone = (client.phone_number || '').toLowerCase();
                  const term = searchTerm.toLowerCase();

                  return fullName.includes(term) ||
                        (client.first_name || '').toLowerCase().includes(term) ||
                        (client.last_name || '').toLowerCase().includes(term) ||
                        email.includes(term) ||
                        phone.includes(term);
                }}
                disabled={consultationPhase !== 'pre_start'}
                hideClearButton={!!selectedClient}
                onClientCreated={handleClientCreated}
              />
              
              {selectedClient && (
                <button
                  type="button"
                  className={`absolute right-2 top-2 rounded-full p-1 transition-colors duration-200 z-20 ${
                    consultationPhase !== 'pre_start' 
                      ? 'text-gray-300 bg-white cursor-not-allowed' 
                      : 'text-gray-400 hover:text-gray-700 bg-white'
                  }`}
                  onClick={handleClearClient}
                  disabled={consultationPhase !== 'pre_start'}
                  aria-label={consultationPhase !== 'pre_start' ? "Cannot clear client after consultation starts" : "Clear selected client"}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </div>

        {consultationPhase === 'pre_start' && (
          // Pre-consultation content
          <>
            <div className="flex flex-col items-center justify-center flex-1">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4">Hello {getUserFirstName()}</h2>
              <p className="text-gray-500 mb-8 sm:mb-12 text-center">Press start to begin consultation</p>
              
              <Button 
                onClick={handleStartConsultation}
                className="rounded-full bg-black hover:bg-black/90 text-white py-4 sm:py-6 px-8 sm:px-12 text-base sm:text-lg w-full max-w-[300px] h-[50px] sm:h-[60px] flex items-center justify-center gap-2"
                disabled={creatingSession || !selectedClient}
              >
                <Mic className="h-4 w-4 sm:h-5 sm:w-5" />
                Start
              </Button>
              
              {!selectedClient && (
                <p className="text-sm text-red-500 mt-2">Please select a client to start</p>
              )}
              
              <div className="mt-12 sm:mt-20 w-full flex flex-col items-center">
                <Collapsible
                  open={encounterSettingsOpen}
                  onOpenChange={setEncounterSettingsOpen}
                  className="w-full max-w-4xl"
                >
                  <CollapsibleTrigger className="flex items-center justify-center mb-4 text-gray-600 w-full">
                    <div className="flex items-center">
                      <svg viewBox="0 0 24 24" fill="none" className={`w-5 h-5 mr-2 text-gray-500 transition-transform ${encounterSettingsOpen ? 'rotate-180' : ''}`}>
                        <path d="M19 9L12 16L5 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span className="text-gray-600">Encounter settings</span>
                    </div>
                  </CollapsibleTrigger>
                  
                  <CollapsibleContent>
                    <div className="flex flex-col sm:flex-row sm:flex-wrap justify-center gap-2 sm:gap-4 mt-4 w-full max-w-4xl">
                      <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                        <SelectTrigger className="bg-gray-100 rounded-full px-4 py-2 flex items-center gap-2 border-none w-full sm:w-[200px]">
                          <BookText className="w-5 h-5 text-gray-700" />
                          <span>Template:</span>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {templates.map(template => (
                            <SelectItem key={template} value={template} className="capitalize">
                              {template}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      {micPermission === true ? (
                        <Select value={selectedMic || ""} onValueChange={setSelectedMic}>
                          <SelectTrigger className="bg-gray-100 rounded-full px-4 py-2 flex items-center gap-2 border-none w-full sm:w-[200px]">
                            <Headphones className="w-5 h-5 text-gray-700" />
                            <SelectValue placeholder="Select microphone" />
                          </SelectTrigger>
                          <SelectContent>
                            {mics.map(mic => (
                              <SelectItem key={mic.deviceId} value={mic.deviceId}>
                                {mic.label || `Microphone ${mics.indexOf(mic) + 1}`}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <div className="bg-yellow-100 text-yellow-800 rounded-full px-4 py-2 flex items-center gap-2 w-full sm:w-[200px]">
                          <Headphones className="w-5 h-5" />
                          <span>Allow mic access</span>
                        </div>
                      )}
                      
                      <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
                        <SelectTrigger className="bg-gray-100 rounded-full px-4 py-2 flex items-center gap-2 border-none w-full sm:w-[200px]">
                          <Languages className="w-5 h-5 text-gray-700" />
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Auto-detect">Auto-detect</SelectItem>
                          <SelectItem value="English">English</SelectItem>
                          <SelectItem value="Arabic">Arabic</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </div>
            </div>
          </>
        )}

        {consultationPhase === 'recording' && (
          // Active consultation content (recording controls)
          <>
            <div className="h-px bg-gray-200 mb-6 w-full"></div>

            {/* Timer and recording controls */}
            <div className="flex flex-col items-center justify-center flex-1 min-h-[300px]">
              <div className="text-3xl sm:text-5xl font-bold mb-4">{formatTime(recordingTime)}</div>

              {/* Debug: Log recording status for UI */}
              {(() => {
                console.log(`[UI Render] isTranscribing: ${isTranscribing}, isPaused: ${isTranscribePaused}, hasStarted: ${hasStartedRecording}`);
                return null;
              })()}

              {isTranscribing && !isTranscribePaused ? (
                <>
                  <div className="text-base sm:text-lg text-gray-500 mb-8 sm:mb-12">Taking Notes...</div>
                  <div className="relative">
                    <Button
                      onClick={handlePauseRecording}
                      className="h-16 w-16 sm:h-20 sm:w-20 rounded-full bg-black hover:bg-black/90 text-white flex items-center justify-center"
                      
                    >
                      <Mic className="h-6 w-6 sm:h-8 sm:w-8" />
                     
                    </Button>
                    <div className="absolute -inset-4 bg-red-200 rounded-full -z-10 opacity-40 animate-pulse"></div>
                  </div>
                </>
              ) : isTranscribePaused ? (
                <>
                  <div className="text-base sm:text-lg text-gray-500 mb-8 sm:mb-12">Muted</div>
                  <Button
                    onClick={handleResumeRecording}
                    className="h-16 w-16 sm:h-20 sm:w-20 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center"
                    disabled={isUploadingTranscription}
                  >
                   <MicOff className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                  </Button>
                </>
              ) : hasStartedRecording ? (
                <>
                  <div className="text-base sm:text-lg text-gray-500 mb-8 sm:mb-12">Starting...</div>
                  <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-full bg-gray-200 flex items-center justify-center">
                    <Loader2 className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400 animate-spin" />
                  </div>
                </>
              ) : (
                <>
                  <div className="text-base sm:text-lg text-gray-500 mb-8 sm:mb-12">Preparing...</div>
                  <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-full bg-gray-200 flex items-center justify-center">
                    <Loader2 className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400 animate-spin" />
                  </div>
                </>
              )}
            </div>

            {/* SOAP Preview Toggle Button */}
            <div className="mt-4 flex justify-center">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSOAPPreview(!showSOAPPreview)}
                className="flex items-center gap-2"
              >
                <FileText className="w-4 h-4" />
                {showSOAPPreview ? 'Hide' : 'Show'} Live SOAP
                {getSOAPCounts(realtimeState.accumulatedSOAP).total > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                    {getSOAPCounts(realtimeState.accumulatedSOAP).total}
                  </span>
                )}
              </Button>
            </div>

            {/* SOAP Preview Panel */}
            {showSOAPPreview && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-gray-700 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Live SOAP Preview
                  </h4>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span className="px-2 py-1 bg-blue-100 rounded">S: {getSOAPCounts(realtimeState.accumulatedSOAP).s}</span>
                    <span className="px-2 py-1 bg-green-100 rounded">O: {getSOAPCounts(realtimeState.accumulatedSOAP).o}</span>
                    <span className="px-2 py-1 bg-purple-100 rounded">P: {getSOAPCounts(realtimeState.accumulatedSOAP).p}</span>
                    {realtimeState.pendingChunks > 0 && (
                      <span className="px-2 py-1 bg-yellow-100 rounded flex items-center gap-1">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        {realtimeState.pendingChunks}
                      </span>
                    )}
                  </div>
                </div>
                <pre className="text-sm text-gray-600 whitespace-pre-wrap max-h-48 overflow-y-auto">
                  {formatSOAPPreview(realtimeState.accumulatedSOAP)}
                </pre>
              </div>
            )}

            {/* Controls at the bottom for active recording phase */}
            <div className="mt-6 sm:mt-8 border-t border-gray-200 pt-4">
              <div className="flex flex-col sm:flex-row sm:justify-between px-2 sm:px-4 py-2 gap-4 sm:gap-0">
                <div className="flex items-center gap-2">
                  <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                    <SelectTrigger className="border-none bg-transparent">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map(template => (
                        <SelectItem key={template} value={template} className="capitalize">
                          {template}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  {micPermission === true ? (
                    <Select value={selectedMic || ""} onValueChange={setSelectedMic}>
                      <SelectTrigger className="border-none bg-transparent">
                        <Headphones className="w-5 h-5 text-gray-700 mr-2" />
                        <SelectValue placeholder="Select microphone" />
                      </SelectTrigger>
                      <SelectContent>
                        {mics.map(mic => (
                          <SelectItem key={mic.deviceId} value={mic.deviceId}>
                            {mic.label || `Microphone ${mics.indexOf(mic) + 1}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="text-yellow-600 flex items-center gap-2">
                      <Headphones className="w-5 h-5" />
                      <span>Allow microphone access</span>
                    </div>
                  )}
                  
                  {/* Finish Note button: always shown in recording phase */}
                  <Button
                    className="w-full sm:w-auto sm:ml-auto bg-black hover:bg-black/90 text-white rounded-full px-4 sm:px-6"
                    onClick={handleFinishNote}
                    disabled={isUploadingTranscription || isFinishing}
                  >
                    {isUploadingTranscription || isFinishing ? (
                      <>
                        <span className="mr-2">Processing...</span>
                        <div className="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin"></div>
                      </>
                    ) : "Finish and create note"}
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ConsultationInterface;